import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { toast } from 'react-toastify';
import {
    fetchFoundItemDiscardReasons,
    addFoundItemDiscardReason,
    updateFoundItemDiscardReason,
    deleteFoundItemDiscardReason
} from './foundItemDiscardReasonAsyncThunk';
import { RequestResults } from '../../common/RequestStatus';
import { DeleteReason, DeleteReasonState } from '../../../types/ReasonTypes';

const initialState: DeleteReasonState = {
    data: [],
    status: RequestResults.idle,
    loading: false,
    error: null,
};

// Slice
const foundItemDiscardReasonSlice = createSlice({
    name: 'foundItemDiscardReasons',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        // Fetch Found Item Discard Reasons
        builder
            .addCase(fetchFoundItemDiscardReasons.pending, (state) => {
                state.status = RequestResults.pending;
                state.error = null;
            })
            .addCase(fetchFoundItemDiscardReasons.fulfilled, (state, action) => {
                state.status = RequestResults.success;
                state.data = action.payload;
            })
            .addCase(fetchFoundItemDiscardReasons.rejected, (state, action) => {
                state.status = RequestResults.failed;
                toast.error(action.payload || 'Failed to fetch found item discard reasons');
                state.error = action.payload || 'Failed to fetch found item discard reasons';
            });

        // Add Found Item Discard Reason
        builder
            .addCase(addFoundItemDiscardReason.pending, (state) => {
                state.loading = true;
            })
            .addCase(addFoundItemDiscardReason.fulfilled, (state, action: PayloadAction<DeleteReason>) => {
                state.loading = false;
                state.data.push(action.payload);
            })
            .addCase(addFoundItemDiscardReason.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to add found item discard reason');
                state.error = action.payload || 'Failed to add found item discard reason';
            });

        // Update Found Item Discard Reason
        builder
            .addCase(updateFoundItemDiscardReason.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateFoundItemDiscardReason.fulfilled, (state, action) => {
                const index = state.data.findIndex((item) => item.id === action.payload.id);
                if (index !== -1) {
                    state.data[index] = action.payload;
                }
                state.loading = false;
            })
            .addCase(updateFoundItemDiscardReason.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to update found item discard reason');
                state.error = action.payload || 'Failed to update found item discard reason';
            });

        // Delete Found Item Discard Reason
        builder
            .addCase(deleteFoundItemDiscardReason.pending, (state) => {
                state.loading = true;
            })
            .addCase(deleteFoundItemDiscardReason.fulfilled, (state, action) => {
                state.data = state.data.filter((item) => item.id !== action.payload.id);
                state.loading = false;
            })
            .addCase(deleteFoundItemDiscardReason.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to delete found item discard reason');
                state.error = action.payload || 'Failed to delete found item discard reason';
            });
    },
});

export const selectFoundItemDiscardReason = (state: RootState) => state.foundItemDiscardReason;

export default foundItemDiscardReasonSlice.reducer;
